import { HotelsAvailabilityActions } from "@b2bportal/core-hotels";
import type { SearchDetails } from "@b2bportal/core-types";
import { ensureExhaustive, getDeviceType } from "@b2bportal/core-utilities";
import {
  type Initial,
  LodgingSelectionEnum,
  type PlaceId,
  Platform,
} from "@b2bportal/lodging-api";
import {
  type InitialResponse,
  type PackageAvailabilityRequest,
  packagesApi,
} from "@b2bportal/packages-api";
import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import {
  PackagesSearchActions,
  type PackagesSearch,
} from "../../../search/store";
import { PackageHotelActions, type TPackagesByLodgingId } from "../slice";
import { fetchPackageHotelAvailabilityMore } from "./fetchPackageHotelAvailabilityMore";

export const fetchPackageHotelAvailability = createAsyncThunk<
  TPackagesByLodgingId,
  { search: PackagesSearch },
  { rejectValue: { error: string } }
>("packages/fetchHotels", async ({ search }, thunkAPI) => {
  const { isDesktopAndUp } = getDeviceType();
  const searchId = uuidv4();

  try {
    const destination = getDestinationByHotelSuggestion(search.destination.id);

    const availabilityRequest: PackageAvailabilityRequest = {
      searchFilters: {
        flightFilter: "NoFilter",
      },
      PackageAvailabilityRequest: "InitialRequest",
      roomCount: search.roomCount,
      dateRange: search.dateRange,
      origin: search.origin.id.code,
      searchToken: undefined,
      travelers: search.guests,
      platform: isDesktopAndUp ? Platform.Desktop : Platform.Mobile,
      destination: destination,
    };

    thunkAPI.dispatch(HotelsAvailabilityActions.handleFetchInitialPending());

    thunkAPI.dispatch(PackagesSearchActions.setSearch(search));

    const hotelsAvailabilitySearch = {
      ...search,
      rooms: search.roomCount,
      guests: {
        adults: search.guests.adultCount,
        children: search.guests.childAges,
      },
      place: search.destination,
      searchType: LodgingSelectionEnum.Place,
      lodgingSelection: null,
    } satisfies SearchDetails;

    thunkAPI.dispatch(
      HotelsAvailabilityActions.setRequestedSearchFromPackages(
        hotelsAvailabilitySearch
      )
    );

    const res =
      await packagesApi(axios).apiV0PackagesAvailabilityPost(
        availabilityRequest
      );

    const initialResponse = res.data as InitialResponse;
    const initialLodgingResponse: Initial =
      initialResponse.product.response.value;

    thunkAPI.dispatch(
      HotelsAvailabilityActions.handleFetchInitialFulfilled({
        ...initialLodgingResponse,
        searchId,
      })
    );

    thunkAPI.dispatch(PackageHotelActions.setMeta(initialResponse));

    if (initialResponse.product.nextPageToken) {
      thunkAPI.dispatch(
        fetchPackageHotelAvailabilityMore({
          nextPageToken: initialResponse.product.nextPageToken.value,
          searchId,
        })
      );
    }

    thunkAPI.dispatch(
      PackageHotelActions.setHotelAvailabilityPackagePricing({
        packagesByLodgingId: Object.fromEntries(
          Object.entries(initialResponse.product.packagesByLodgingId).map(
            ([key, lodging]) => [key, lodging.packageSummary]
          )
        ),
        searchId,
      })
    );

    return {
      packagesByLodgingId: Object.fromEntries(
        Object.entries(initialResponse.product.packagesByLodgingId).map(
          ([key, lodging]) => [key, lodging.packageSummary]
        )
      ),
      searchId,
    };
  } catch (e) {
    console.error("Failed to fetch hotels:", e);
    const errorMessage = e instanceof Error ? e.message : String(e);
    thunkAPI.dispatch(
      HotelsAvailabilityActions.handleFetchInitialRejected({
        error: errorMessage,
      })
    );
    return thunkAPI.rejectWithValue({ error: errorMessage });
  }
});

export const getDestinationByHotelSuggestion = (place: PlaceId) => {
  switch (place.Id) {
    case "HotelMarket":
      throw new Error("Unsupported search types");

    case "Lodgings": {
      return place.lodgingSelection;
    }

    default:
      ensureExhaustive(place);
  }
};
